import React, { useState, useEffect } from "react";
import {
  Box, Table, TableBody, TableCell, TableHead, TableRow,
  Typography, Card, Button, TableContainer, Paper,
  CircularProgress, Alert, MenuItem, Select, TextField,
  Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, Tooltip, Snackbar, FormControl, InputLabel, Chip,
} from "@mui/material";
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import ImageIcon from '@mui/icons-material/Image';
import Swal from 'sweetalert2';
import axios from "axios";
import { useSelector } from 'react-redux';
import { apiHost } from '../utils/apiHost';

const getApiBaseUrl = () => {
  const protocol = window.location.protocol === "https:" ? "https" : "http";
  const baseUrl = apiHost().replace(/^https?:\/\/+/, "");
  return `${protocol}://${baseUrl}`;
};

const DEFAULT_NONCASH_OPTIONS = ['BCA', 'Transfer Bank', 'QRIS Statis', 'GoPay', 'OVO', 'DANA'];

export const NonCashMethodData = () => {
  const user = useSelector((state) => state.auth.user);
  const [methods, setMethods] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [formData, setFormData] = useState({ name: '', cabanguuid: '' });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [removeImage, setRemoveImage] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const apiBaseUrl = getApiBaseUrl();

  const fetchMethods = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${apiBaseUrl}/getnoncashmethod`, { withCredentials: true });
      setMethods(response.data.data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Gagal mengambil data metode non cash');
    } finally {
      setLoading(false);
    }
  };

  const fetchBranches = async () => {
    try {
      const response = await axios.get(`${apiBaseUrl}/cabang`, { withCredentials: true });
      const data = response.data.data || [];
      const adminBranchUuid = user?.cabang?.uuid || user?.cabanguuid || null;
      if (user && user.role === 'admin') {
        let myBranch = data.find(b => b.uuid === adminBranchUuid);
        if (!myBranch && user?.cabang) myBranch = { uuid: user.cabang.uuid, namacabang: user.cabang.namacabang };
        setBranches(myBranch ? [myBranch] : []);
      } else {
        setBranches(data);
      }
    } catch (err) {
      console.error('Error fetching branches:', err);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    setImageFile(file || null);
    setImagePreview(file ? URL.createObjectURL(file) : null);
    setRemoveImage(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = new FormData();
      payload.append('name', formData.name);
      payload.append('cabanguuid', formData.cabanguuid || '');
      if (imageFile) payload.append('image', imageFile);
      if (removeImage) payload.append('removeImage', 'true');

      const config = { headers: { 'Content-Type': 'multipart/form-data' }, withCredentials: true };

      if (isEditing) {
        await axios.put(`${apiBaseUrl}/updatenoncashmethod/${currentItem.uuid}`, payload, config);
        setSnackbarMessage('Metode Non Cash berhasil diperbarui');
      } else {
        await axios.post(`${apiBaseUrl}/createnoncashmethod`, payload, config);
        setSnackbarMessage('Metode Non Cash berhasil ditambahkan');
      }
      setSnackbarOpen(true);
      setOpenDialog(false);
      fetchMethods();
    } catch (err) {
      setSnackbarMessage(err.response?.data?.message || 'Operasi gagal');
      setSnackbarOpen(true);
    }
  };

  const handleDelete = (uuid) => {
    Swal.fire({
      title: 'Yakin ingin menghapus?',
      text: 'Data tidak dapat dikembalikan!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Ya, hapus!',
      cancelButtonText: 'Batal'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await axios.delete(`${apiBaseUrl}/deletenoncashmethod/${uuid}`, { withCredentials: true });
          setSnackbarMessage('Metode Non Cash berhasil dihapus');
          setSnackbarOpen(true);
          fetchMethods();
        } catch (err) {
          setSnackbarMessage(err.response?.data?.message || 'Gagal menghapus metode');
          setSnackbarOpen(true);
        }
      }
    });
  };

  const handleEditClick = (item) => {
    setCurrentItem(item);
    setFormData({ name: item.name, cabanguuid: item.cabanguuid || '' });
    setImageFile(null);
    setImagePreview(null);
    setRemoveImage(false);
    setIsEditing(true);
    setOpenDialog(true);
  };

  const handleNew = () => {
    setCurrentItem(null);
    const defaultCabang = user?.role === 'admin'
      ? (user?.cabang?.uuid || user?.cabanguuid || '')
      : '';
    setFormData({ name: '', cabanguuid: defaultCabang });
    setImageFile(null);
    setImagePreview(null);
    setRemoveImage(false);
    setIsEditing(false);
    setOpenDialog(true);
  };

  const handleQuickAdd = (name) => {
    const defaultCabang = user?.role === 'admin'
      ? (user?.cabang?.uuid || user?.cabanguuid || '')
      : '';
    setFormData({ name, cabanguuid: defaultCabang });
    setImageFile(null);
    setImagePreview(null);
    setRemoveImage(false);
    setIsEditing(false);
    setCurrentItem(null);
    setOpenDialog(true);
  };

  useEffect(() => {
    fetchMethods();
    fetchBranches();
  }, [user]);

  const isAdmin = user && user.role === 'admin';
  const isSuperadmin = user && user.role === 'superadmin';

  if (!isAdmin && !isSuperadmin) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Anda tidak memiliki akses ke halaman ini.</Alert>
      </Box>
    );
  }

  const currentImageUrl = currentItem?.imagePath ? `${apiBaseUrl}/uploads/${currentItem.imagePath}` : null;

  return (
    <Box sx={{ p: 3 }}>
      <Card sx={{ mb: 4, p: 3, boxShadow: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" component="h1">Metode Non Cash</Typography>
          <Box>
            <Button variant="contained" color="primary" startIcon={<RefreshIcon />} onClick={fetchMethods} sx={{ mr: 2 }}>
              Refresh
            </Button>
            <Button variant="contained" color="success" startIcon={<AddIcon />} onClick={handleNew}>
              Tambah Metode
            </Button>
          </Box>
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>Tambah Cepat:</Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {DEFAULT_NONCASH_OPTIONS.map((opt) => (
              <Button key={opt} variant="outlined" size="small" onClick={() => handleQuickAdd(opt)}>
                + {opt}
              </Button>
            ))}
          </Box>
        </Box>

        <Alert severity="info" sx={{ mb: 2 }}>
          Metode ini hanya tampil di aplikasi POS & dashboard web. Di web menu dan struk, pembayaran non cash selalu tampil sebagai "Non Cash" saja.
        </Alert>

        {isSuperadmin && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Metode tanpa cabang (kosong) akan tampil di semua cabang sebagai pilihan default.
          </Alert>
        )}

        {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

        <TableContainer component={Paper} sx={{ boxShadow: 2 }}>
          <Table sx={{ minWidth: 500 }}>
            <TableHead sx={{ bgcolor: 'primary.main' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', color: 'white', width: 60 }}>ID</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: 'white', width: 70 }}>Gambar</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: 'white' }}>Nama Metode</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: 'white' }}>Cabang</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: 'white' }} align="center">Aksi</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center"><CircularProgress /></TableCell>
                </TableRow>
              ) : methods.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">Belum ada data metode non cash</TableCell>
                </TableRow>
              ) : (
                methods.map((item, index) => (
                  <TableRow key={item.uuid} hover>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>
                      {item.imagePath ? (
                        <Box
                          component="img"
                          src={`${apiBaseUrl}/uploads/${item.imagePath}`}
                          alt={item.name}
                          sx={{ width: 36, height: 36, objectFit: 'contain', border: '1px solid #ddd', borderRadius: 1 }}
                        />
                      ) : (
                        <ImageIcon sx={{ color: '#ccc' }} />
                      )}
                    </TableCell>
                    <TableCell>{item.name}</TableCell>
                    <TableCell>
                      {item.cabanguuid === null
                        ? <Chip label="Semua Cabang" color="primary" size="small" />
                        : item.Cabang?.namacabang || '-'}
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Edit">
                        <IconButton
                          color="primary"
                          onClick={() => handleEditClick(item)}
                          sx={{ mr: 1 }}
                          disabled={isAdmin && item.cabanguuid === null}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Hapus">
                        <IconButton
                          color="error"
                          onClick={() => handleDelete(item.uuid)}
                          disabled={isAdmin && item.cabanguuid === null}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>{isEditing ? 'Edit Metode Non Cash' : 'Tambah Metode Non Cash'}</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent sx={{ minWidth: 350 }}>
            <TextField
              autoFocus
              margin="dense"
              name="name"
              label="Nama Metode"
              type="text"
              fullWidth
              variant="outlined"
              value={formData.name}
              onChange={handleInputChange}
              required
              sx={{ mb: 2 }}
              placeholder="Contoh: BCA, Transfer Bank, QRIS Statis"
            />
            {isSuperadmin && (
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Cabang (kosongkan untuk semua cabang)</InputLabel>
                <Select
                  name="cabanguuid"
                  value={formData.cabanguuid}
                  onChange={handleInputChange}
                  label="Cabang (kosongkan untuk semua cabang)"
                >
                  <MenuItem value="">Semua Cabang (Global)</MenuItem>
                  {branches.map((branch) => (
                    <MenuItem key={branch.uuid} value={branch.uuid}>{branch.namacabang}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {isAdmin && (
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Cabang</InputLabel>
                <Select
                  name="cabanguuid"
                  value={formData.cabanguuid}
                  onChange={handleInputChange}
                  label="Cabang"
                  required
                  disabled
                >
                  {branches.map((branch) => (
                    <MenuItem key={branch.uuid} value={branch.uuid}>{branch.namacabang}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <Typography variant="subtitle2" gutterBottom>Gambar/QR (opsional)</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
              <Box sx={{
                width: 60, height: 60, border: '1px solid #ddd', borderRadius: 1,
                display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', bgcolor: '#fafafa'
              }}>
                {(imagePreview || (!removeImage && currentImageUrl)) ? (
                  <img src={imagePreview || currentImageUrl} alt="Preview" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                ) : (
                  <ImageIcon sx={{ color: '#ccc' }} />
                )}
              </Box>
              <Button variant="outlined" component="label" size="small">
                Pilih Gambar
                <input type="file" accept="image/*" hidden onChange={handleImageChange} />
              </Button>
              {!removeImage && currentImageUrl && !imageFile && (
                <Button variant="outlined" color="error" size="small" onClick={() => setRemoveImage(true)}>
                  Hapus Gambar
                </Button>
              )}
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setOpenDialog(false)} color="inherit">Batal</Button>
            <Button type="submit" variant="contained" color="primary">
              {isEditing ? 'Update' : 'Simpan'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
    </Box>
  );
};

export default NonCashMethodData;
